// React component

import { motion } from "framer-motion";
import { Gauge, Zap, Clock, Server } from "lucide-react";
import { useState, useEffect } from "react";

interface MetricProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  unit: string;
  color: string;
}

const MetricCard: React.FC<MetricProps> = ({
  icon: Icon,
  label,
  value,
  unit,
  color,
}) => (
  <motion.div
    className="rounded-lg p-4 transition-transform duration-300"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.02 }}
    data-oid="2sdx28r"
  >
    <div className="flex items-center gap-3" data-oid=":80a9gi">
      <div className={`p-2 rounded-lg ${color}`} data-oid="vptcmr7">
        <Icon className="h-5 w-5" data-oid="6qrmn-j" />
      </div>
      <div data-oid="h:3c:0d">
        <p className="text-sm text-muted-foreground" data-oid="ycxaddo">
          {label}
        </p>
        <p className="text-lg font-semibold" data-oid="kbt.-e9">
          {value}
          <span
            className="text-sm text-muted-foreground ml-1"
            data-oid="cdbymc6"
          >
            {unit}
          </span>
        </p>
      </div>
    </div>
  </motion.div>
);

const PerformanceMetrics = () => {
  const [metrics, setMetrics] = useState({
    loadTime: 0.8,
    uptime: 99.9,
    responseTime: 150,
    throughput: 1200,
  });

  const [width, setWidth] = useState(800); // Default width
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Update width on mount
    setWidth(window.innerWidth);
    setIsMobile(window.innerWidth < 768); // Set mobile state based on width

    // Handle window resize
    const handleResize = () => {
      setWidth(window.innerWidth);
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics((prev) => ({
        loadTime: +(prev.loadTime + (Math.random() * 0.2 - 0.1)).toFixed(1),
        uptime: +(prev.uptime + (Math.random() * 0.2 - 0.1)).toFixed(1),
        responseTime: Math.max(
          100,
          Math.min(200, prev.responseTime + Math.random() * 20 - 10),
        ),
        throughput: Math.max(
          1000,
          Math.min(1500, prev.throughput + Math.random() * 100 - 50),
        ),
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-4" data-oid="wlw_2n6">
      <div className="grid grid-cols-2 gap-4" data-oid="w56g5ci">
        <MetricCard
          icon={Clock}
          label="Load Time"
          value={metrics.loadTime}
          unit="s"
          color="bg-blue-500/10 text-blue-500"
          data-oid="x9ecvfu"
        />

        <MetricCard
          icon={Gauge}
          label="Uptime"
          value={metrics.uptime}
          unit="%"
          color="bg-green-500/10 text-green-500"
          data-oid="ay5an75"
        />

        <MetricCard
          icon={Zap}
          label="Response Time"
          value={Math.round(metrics.responseTime)}
          unit="ms"
          color="bg-yellow-500/10 text-yellow-500"
          data-oid="9g1gbmh"
        />

        <MetricCard
          icon={Server}
          label="Throughput"
          value={Math.round(metrics.throughput)}
          unit="req/s"
          color="bg-purple-500/10 text-purple-500"
          data-oid="wfs-:wu"
        />
      </div>

      {/* Performance Graph - Hidden on mobile */}
      {!isMobile && (
        <motion.div
          className="h-32 relative mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          data-oid="debc-zf"
        >
          <div className="absolute inset-0" data-oid="7:7aul_">
            <svg className="w-full h-full" data-oid="v9dplqo">
              <defs data-oid="kgqsh-6">
                <linearGradient
                  id="gradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                  data-oid="yont97y"
                >
                  <stop
                    offset="0%"
                    stopColor="rgb(59, 130, 246)"
                    stopOpacity="0.5"
                    data-oid="derbwfo"
                  />
                  <stop
                    offset="100%"
                    stopColor="rgb(59, 130, 246)"
                    stopOpacity="0"
                    data-oid="sxeujuj"
                  />
                </linearGradient>
              </defs>
              <motion.path
                d={`M 0 ${80 - metrics.loadTime * 50} 
                  C ${width / 4} ${90 - metrics.responseTime / 4}, 
                    ${width / 2} ${70 - metrics.uptime / 2}, 
                    ${width} ${60 - metrics.throughput / 20}`}
                fill="none"
                stroke="rgb(59, 130, 246)"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, repeat: Infinity }}
                data-oid="k_z0.gx"
              />
            </svg>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PerformanceMetrics;
